import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { CreditCard, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useActiveClinic } from '@/lib/activeClinic';
import { useAuth } from '@/lib/auth';
import { useClinic, useUpdateClinicSettings } from '@/hooks/useClinicSettings';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { SectionCard, SaveBar, FieldWithIcon, PlainField } from '@/pages/settings/ClinicGeneral';
import type { Json } from '@/types/database.generated';

const CURRENCIES = ['DKK', 'EUR', 'SEK', 'NOK', 'USD'] as const;
const LOCALES = ['da', 'en', 'de', 'sv', 'no'] as const;

export function ClinicBilling() {
  const { t } = useTranslation();
  const { activeClinicId } = useActiveClinic();
  const { isClinicAdmin, isPlatformAdmin } = useAuth();
  const { data: clinic, isLoading, error } = useClinic(activeClinicId);
  const update = useUpdateClinicSettings(activeClinicId);

  const canEdit = Boolean(activeClinicId && (isPlatformAdmin || isClinicAdmin(activeClinicId)));

  const [vatNumber, setVatNumber] = useState('');
  const [vatRate, setVatRate] = useState('');
  const [currency, setCurrency] = useState<string>('DKK');
  const [locale, setLocale] = useState<string>('da');
  const [paymentTermsDays, setPaymentTermsDays] = useState('');
  const [invoicePrefix, setInvoicePrefix] = useState('');
  const [invoiceStartNumber, setInvoiceStartNumber] = useState('');
  const [bankName, setBankName] = useState('');
  const [regNumber, setRegNumber] = useState('');
  const [accountNumber, setAccountNumber] = useState('');

  useEffect(() => {
    if (!clinic) return;
    setVatNumber(clinic.vat_number ?? '');
    setVatRate(clinic.vat_rate_percent != null ? String(clinic.vat_rate_percent) : '');
    setCurrency(clinic.default_currency);
    setLocale(clinic.default_locale);
    setPaymentTermsDays(clinic.payment_terms_days != null ? String(clinic.payment_terms_days) : '14');
    setInvoicePrefix(clinic.invoice_prefix ?? 'FAK');
    setInvoiceStartNumber(clinic.invoice_start_number != null ? String(clinic.invoice_start_number) : '1000');
    
    const bankInfo = (clinic.bank_info as Record<string, unknown>) ?? {};
    setBankName(String(bankInfo.bank ?? ''));
    setRegNumber(String(bankInfo.reg_number ?? ''));
    setAccountNumber(String(bankInfo.account_number ?? ''));
  }, [clinic]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }
  if (error) return <ErrorAlert message={t('settings.load_error')} />;
  if (!clinic) return <ErrorAlert message={t('settings.no_clinic')} />;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const payload: Record<string, Json> = {
      vat_number: vatNumber.trim() || null,
      default_currency: currency,
      default_locale: locale,
    };
    if (vatRate.trim() === '') {
      payload.vat_rate_percent = null;
    } else {
      const n = Number(vatRate);
      if (!Number.isFinite(n) || n < 0 || n > 100) {
        toast.error(t('settings.save_error_toast'));
        return;
      }
      payload.vat_rate_percent = n;
    }
    
    // Payment terms days
    if (paymentTermsDays.trim() !== '') {
      const days = Number(paymentTermsDays);
      if (!Number.isFinite(days) || days < 0 || days > 365) {
        toast.error(t('settings.billing.payment_terms_error'));
        return;
      }
      payload.payment_terms_days = days;
    }

    // Invoice prefix
    if (invoicePrefix.trim() !== '') {
      payload.invoice_prefix = invoicePrefix.trim().toUpperCase();
    }

    // Invoice start number
    if (invoiceStartNumber.trim() !== '') {
      const startNum = Number(invoiceStartNumber);
      if (!Number.isFinite(startNum) || startNum < 1) {
        toast.error(t('settings.billing.invoice_start_number_error'));
        return;
      }
      payload.invoice_start_number = startNum;
    }
    
    const bankInfo: Record<string, string> = {};
    if (bankName.trim()) bankInfo.bank = bankName.trim();
    if (regNumber.trim()) bankInfo.reg_number = regNumber.trim();
    if (accountNumber.trim()) bankInfo.account_number = accountNumber.trim();
    payload.bank_info = bankInfo as Json;
    
    try {
      await update.mutateAsync(payload);
      toast.success(t('settings.saved_toast'));
    } catch (err) {
      console.error('update_clinic_settings (billing) failed', err);
      toast.error(t('settings.save_error_toast'));
    }
  };

  const subscriptionStatus = clinic.subscription_status;

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      <SectionCard
        title={t('settings.billing.title')}
        subtitle={t('settings.billing.subtitle')}
        readOnly={!canEdit}
      >
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <FieldWithIcon
            id="vat-number"
            label={t('settings.billing.vat_number')}
            icon={CreditCard}
            value={vatNumber}
            onChange={setVatNumber}
            disabled={!canEdit}
          />
          <PlainField
            id="vat-rate"
            label={t('settings.billing.vat_rate')}
            value={vatRate}
            onChange={setVatRate}
            type="number"
            disabled={!canEdit}
          />

          <div>
            <label
              htmlFor="default-currency"
              className="block text-sm font-medium text-gray-700 dark:text-gray-200"
            >
              {t('settings.billing.default_currency')}
            </label>
            <select
              id="default-currency"
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              disabled={!canEdit}
              className="mt-1 block h-[44px] w-full rounded-xl border border-gray-300 bg-white px-3 text-gray-900 focus:border-indigo-500 focus:ring-indigo-500 disabled:opacity-60 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            >
              {CURRENCIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              htmlFor="default-locale"
              className="block text-sm font-medium text-gray-700 dark:text-gray-200"
            >
              {t('settings.billing.default_locale')}
            </label>
            <select
              id="default-locale"
              value={locale}
              onChange={(e) => setLocale(e.target.value)}
              disabled={!canEdit}
              className="mt-1 block h-[44px] w-full rounded-xl border border-gray-300 bg-white px-3 text-gray-900 focus:border-indigo-500 focus:ring-indigo-500 disabled:opacity-60 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            >
              {LOCALES.map((l) => (
                <option key={l} value={l}>
                  {l.toUpperCase()}
                </option>
              ))}
            </select>
          </div>
        </div>
      </SectionCard>

      <SectionCard
        title={t('settings.billing.invoice_settings_title')}
        subtitle={t('settings.billing.invoice_settings_subtitle')}
        readOnly={!canEdit}
      >
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
          <PlainField
            id="payment-terms-days"
            label={t('settings.billing.payment_terms_days')}
            value={paymentTermsDays}
            onChange={setPaymentTermsDays}
            type="number"
            disabled={!canEdit}
            placeholder="14"
          />
          <PlainField
            id="invoice-prefix"
            label={t('settings.billing.invoice_prefix')}
            value={invoicePrefix}
            onChange={setInvoicePrefix}
            disabled={!canEdit}
            placeholder="FAK"
          />
          <PlainField
            id="invoice-start-number"
            label={t('settings.billing.invoice_start_number')}
            value={invoiceStartNumber}
            onChange={setInvoiceStartNumber}
            type="number"
            disabled={!canEdit}
            placeholder="1000"
          />
        </div>
      </SectionCard>

      <SectionCard
        title={t('settings.billing.bank_info_title')}
        subtitle={t('settings.billing.bank_info_subtitle')}
        readOnly={!canEdit}
      >
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <PlainField
            id="bank-name"
            label={t('settings.billing.bank_name')}
            value={bankName}
            onChange={setBankName}
            disabled={!canEdit}
            placeholder={t('settings.billing.bank_name_placeholder')}
          />
          <PlainField
            id="reg-number"
            label={t('settings.billing.reg_number')}
            value={regNumber}
            onChange={setRegNumber}
            disabled={!canEdit}
            placeholder={t('settings.billing.reg_number_placeholder')}
          />
          <PlainField
            id="account-number"
            label={t('settings.billing.account_number')}
            value={accountNumber}
            onChange={setAccountNumber}
            disabled={!canEdit}
            placeholder={t('settings.billing.account_number_placeholder')}
          />
        </div>
      </SectionCard>

      <SectionCard
        title={t('settings.billing.subscription')}
        subtitle={t('settings.billing.subscription_managed_externally')}
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <span className="block text-sm font-medium text-gray-700 dark:text-gray-200">
              {t('settings.billing.subscription_status')}
            </span>
            <span
              className={
                'mt-1 inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ' +
                statusBadgeClass(subscriptionStatus)
              }
            >
              {t(`settings.billing.subscription_status_${subscriptionStatus}`)}
            </span>
          </div>
          {clinic.trial_ends_at && (
            <div>
              <span className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                {t('settings.billing.trial_ends_at')}
              </span>
              <span className="mt-1 inline-block text-sm text-gray-900 dark:text-white">
                {new Date(clinic.trial_ends_at).toLocaleDateString()}
              </span>
            </div>
          )}
        </div>
      </SectionCard>

      <SaveBar canEdit={canEdit} pending={update.isPending} />
    </form>
  );
}

function statusBadgeClass(status: string): string {
  switch (status) {
    case 'active':
      return 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300';
    case 'trial':
      return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300';
    case 'paused':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300';
    case 'expired':
    case 'cancelled':
      return 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
  }
}
